import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook that tracks container width changes using ResizeObserver API.
 * Provides reactive container width updates when the observed element is resized.
 *
 * @param {React.RefObject} containerRef - React ref object pointing to the DOM element to observe
 * @returns {{containerWidth: number}} Object containing the current container width in pixels
 */
export default function useContainerResize(containerRef) {
  const [containerWidth, setContainerWidth] = useState(0);
  const resizeObserver = useRef();

  useEffect(() => {
    const container = containerRef?.current;

    if (!container) return;
    if (resizeObserver.current) resizeObserver.current.disconnect();

    resizeObserver.current = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.current.observe(container);
    setContainerWidth(container.offsetWidth);

    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, [containerRef]);

  return { containerWidth };
}
